import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ArrowLeft, Brain, Sparkles, Users, Zap, MessageSquare, Layers } from 'lucide-react';
import HeroSlider from '../components/shared/HeroSlider';
import Footer from '../components/shared/Footer';

export default function About() {
  const { t, i18n } = useTranslation('about');
  const isRTL = i18n.language === 'ar';

  // Hero slides data
  const heroSlides = [
    {
      title: t('hero.title'),
      highlight: t('hero.titleHighlight'),
      description: t('hero.description'),
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    },
    {
      title: t('hero.title'),
      highlight: t('hero.titleHighlight'),
      description: t('hero.description'),
      image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    },
  ];

  return (
    <div className={`min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 flex flex-col ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* Hero Slider - Same as Homepage */}
      <HeroSlider slides={heroSlides} currentPage="about" />

      {/* The Meaning Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                {/* Faheem Card */}
                <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                    <div className="relative bg-white p-8 rounded-xl border border-slate-100 shadow-lg h-full flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                            <Brain className="w-8 h-8 text-blue-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-slate-900">{t('meaning.faheem.title')}</h2>
                        <Badge className="bg-blue-50 text-blue-700 border-none">{t('meaning.faheem.badge')}</Badge>
                        <p className="text-slate-600 leading-relaxed">
                            {t('meaning.faheem.description')}
                            <span className="font-semibold text-slate-800"> {t('meaning.faheem.highlight')}</span>
                            {t('meaning.faheem.descriptionEnd')}
                        </p>
                    </div>
                </div>

                {/* GPT Card */}
                <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-purple-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                    <div className="relative bg-white p-8 rounded-xl border border-slate-100 shadow-lg h-full flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-2">
                            <Sparkles className="w-8 h-8 text-indigo-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-slate-900">{t('meaning.gpt.title')}</h2>
                        <Badge className="bg-indigo-50 text-indigo-700 border-none">{t('meaning.gpt.badge')}</Badge>
                        <p className="text-slate-600 leading-relaxed">
                            {t('meaning.gpt.description')} <span className="font-semibold text-slate-800">{t('meaning.gpt.highlight')}</span>
                            {t('meaning.gpt.descriptionEnd')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Philosophy / Facilitator Section */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto text-center space-y-12">
            <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('philosophy.title')}</h2>
                <p className="text-slate-300 text-lg max-w-3xl mx-auto leading-relaxed">
                    {t('philosophy.description')}
                    <span className="text-blue-400 font-semibold"> {t('philosophy.teachingAssistant')}</span>
                    {t('philosophy.descriptionEnd')}
                </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
                <Badge className="bg-slate-800 text-blue-400 border-slate-700 px-4 py-2 text-sm">{t('philosophy.cards.guidance.title')}</Badge>
                <Badge className="bg-slate-800 text-blue-400 border-slate-700 px-4 py-2 text-sm">{t('philosophy.cards.attention.title')}</Badge>
                <Badge className="bg-slate-800 text-blue-400 border-slate-700 px-4 py-2 text-sm">{t('philosophy.cards.productivity.title')}</Badge>
            </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
            <h2 className={`text-3xl font-bold mb-12 text-center text-slate-900`}>{t('features.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Feature 1 - Interactive Learning */}
                <Card className="border-slate-100 shadow-sm hover:border-blue-200 hover:shadow-md transition-all" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <CardHeader>
                        <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 mb-2">
                                <Users className="w-5 h-5" />
                            </div>
                        </div>
                        <CardTitle>{t('features.interactive.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600 text-sm">
                            {t('features.interactive.description')}
                        </p>
                    </CardContent>
                </Card>

                {/* Feature 2 - Personalized Paths */}
                <Card className="border-slate-100 shadow-sm hover:border-blue-200 hover:shadow-md transition-all" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <CardHeader>
                        <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-2">
                                <Brain className="w-5 h-5" />
                            </div>
                        </div>
                        <CardTitle>{t('features.personalized.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600 text-sm">
                            {t('features.personalized.description')}
                        </p>
                    </CardContent>
                </Card>

                {/* Feature 3 - Teaching Assistant */}
                <Card className="border-slate-100 shadow-sm hover:border-blue-200 hover:shadow-md transition-all" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <CardHeader>
                        <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 mb-2">
                                <Sparkles className="w-5 h-5" />
                            </div>
                        </div>
                        <CardTitle>{t('features.assistant.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600 text-sm">
                            {t('features.assistant.description')}
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
      </section>

      {/* Footer - Same as Homepage */}
      <Footer />
    </div>
  );
}
